import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useEffect, useState } from "react";

import network from "../assets/network.png";
import integrity from "../assets/integrity.png";
import innovation from "../assets/innovation.png";
import capital from "../assets/capital.png";

export default function About() {

  /* ================= SCREEN ================= */
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* ================= MOTION ================= */

  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const rotate = useMotionValue(0);
  const floatY = useMotionValue(0);
  const glow = useMotionValue(0);

  const mouseX = useSpring(rawX, { stiffness: 35, damping: 20 });
  const mouseY = useSpring(rawY, { stiffness: 35, damping: 20 });

  const glowFilter = useTransform(
    glow,
    [0, 1],
    [
      "drop-shadow(0 0 8px rgba(34,197,94,0.25))",
      "drop-shadow(0 0 26px rgba(34,197,94,0.45))",
    ]
  );

  /* ================= MOUSE (DESKTOP ONLY) ================= */

  useEffect(() => {
    if (!isDesktop) return;

    const handleMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 40;
      const y = (e.clientY / window.innerHeight - 0.5) * 40;
      rawX.set(x);
      rawY.set(y);
    };

    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, [isDesktop]);

  /* ================= CINEMATIC LOOP ================= */

  useEffect(() => {
    let frame;
    let last = performance.now();
    let floatAngle = 0;
    let glowAngle = 0;

    const loop = (now) => {
      const delta = now - last;
      last = now;

      rotate.set((rotate.get() + delta * 0.002) % 360);

      floatAngle += delta * 0.00015;
      floatY.set(Math.sin(floatAngle) * 6);

      glowAngle += delta * 0.002;
      glow.set((Math.sin(glowAngle) + 1) / 2);

      frame = requestAnimationFrame(loop);
    };

    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, []);

  /* ================= UI ================= */

  return (
    <section id="about" className="bg-white py-14 sm:py-16 md:py-20 overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        <h3 className="text-center font-bold text-gray-900 mb-12 sm:mb-16 text-2xl sm:text-3xl md:text-4xl">
          About Us
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-start">


          {/* ================= LEFT CONTENT ================= */}
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">
              About <span className="text-primary">Yield Ventures</span>
            </h2>

            <p className="mt-4 text-gray-600 text-sm sm:text-base text-justify">
              At Yield Ventures, we believe that true value isn't just found; it is forged.
            </p>

            <p className="mt-4 text-gray-600 text-sm sm:text-base text-justify">
              ​We are not passive observers of the market; we are active participants in the future of finance and technology. Born from a deep understanding of capital markets and an unyielding commitment to innovation, Yield Ventures exists to bridge the gap between visionary ideas and tangible reality.
            </p>

            <p className="mt-4 text-gray-600 text-sm sm:text-base text-justify">
              <strong>Who We Are?</strong><br />
              We are a sector-agnostic Venture Capital firm with a distinct edge in Fintech, Media. We understand that the next unicorn won't just look like a bank or a software company—it will be a hybrid of community, content, and commerce.
            </p>

            <p className="mt-4 text-gray-600 text-sm sm:text-base text-justify">
              <strong>Our Approach</strong><br />
              Capital is a commodity; conviction is rare. We partner with founders at the earliest stages, providing not just liquidity, but the structural support needed to navigate complex regulatory landscapes and volatile market cycles.
            </p>

            <p className="mt-4 text-gray-600 text-sm sm:text-base text-justify">
              <strong>Why Yield?</strong><br />
              Because we measure our success by yours. We leverage our proprietary network of market veterans and industry analysts to ensure that when you are ready to scale, the market is ready for you.
            </p>

            <p className="mt-4 text-gray-600 text-sm sm:text-base text-justify">
              <strong>Our Mission</strong><br />
              To cultivate a portfolio where innovation yields generational impact.
            </p>
          </div>

          {/* ================= RIGHT ================= */}
          <div className="relative flex flex-col items-center">

            <motion.img
  src={network}
  alt="Network"
  className="
  hidden lg:block
  w-[220px] sm:w-[260px] md:w-[300px] lg:w-[380px] xl:w-[420px]
  mb-8
  lg:absolute lg:top-2 lg:left-[480px] lg:-translate-x-1/2
  pointer-events-none
"

  style={{
    x: isDesktop ? mouseX : 0,
    y: isDesktop ? mouseY : 0,
    translateY: floatY,
    rotateZ: rotate,
    rotateX: isDesktop ? -8 : 0,
    rotateY: isDesktop ? 8 : 0,
    filter: glowFilter,
  }}
/>


            <div className="w-full space-y-4 sm:space-y-5 mt-4 lg:mt-32">
              <Card icon={integrity} title="Market Integrity"
                text="We uphold the highest standards of ethics and transparency in all our operations." />

              <Card icon={innovation} title="Innovation"
                text="Continuously evolving our strategies to stay ahead of market trends and opportunities." />

              <Card icon={capital} title="Capital Creation"
                text="Generating sustainable returns while contributing to broader economic growth." />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

/* ================= CARD ================= */

function Card({ icon, title, text }) {
  return (
    <div className="flex gap-4 p-4 sm:p-5 md:p-6 rounded-2xl border bg-white shadow-sm hover:shadow-lg transition">
      <img src={icon} alt="" className="h-9 w-9 sm:h-10 sm:w-10" />
      <div>
        <h4 className="font-semibold text-sm sm:text-base">{title}</h4>
        <p className="text-xs sm:text-sm text-gray-600">{text}</p>
      </div>
    </div>
  );
}
